// Libs
import classNames from 'classnames/bind';
import { Avatar, Button, Flex, Heading, IconRefreshLine, Spinner, Text } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import moment from 'moment';
// Components, Layouts, Pages
import CourseTable from './component/courseTable/CourseTable';
// Others
import { studentRoute } from '~/utils/routes/student';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { getListCourses } from '~/thunks/course/courseThunk';
import { DATE_FORMAT, EMPTY_STRING } from '~/utils/constants/common';
import { IModule } from '~/utils/interface/course';
// Styles, images, icons
import styles from './Course.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const Course = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'common/course']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const courses = useAppSelector((state) => state.courseState.courses);
  const nextCourse = useAppSelector((state) => state.courseState.nextCourses);
  //#endregion Selector

  //#region Declare State
  const formattedDate = nextCourse?.date ? moment.utc(nextCourse.date).local().format(DATE_FORMAT.DATE) : EMPTY_STRING;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListCourses();
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleClickCourseItem = (courseId?: string, moduleId?: string) => {
    navigate(`${studentRoute.base}${studentRoute.course}/${courseId}${studentRoute.module}/${moduleId}`);
  };

  const handleGetListCourses = async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      await dispatch(getListCourses()).unwrap();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={cx('centerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center' margin='medium 0'>
            <Button
              onClick={handleGetListCourses}
              size='medium'
              color='primary'
              margin='x-large 0 0 0'
              renderIcon={<IconRefreshLine />}
            >
              {t('retry_button_label', { ns: 'common/base' })}
            </Button>
          </Flex>
        </div>
      );
    }

    if (!nextCourse) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center' alignItems='center' margin='xx-large 0'>
            <Text variant='contentSmall'>{t('course_detail_no_lesson_available', { ns: 'common/home' })}</Text>
          </Flex>
        </div>
      );
    }

    return (
      <>
        <div className={cx('topContainer')}>
          <Flex display='flex' direction='row' alignItems='center' justifyItems='center' width={'100%'}>
            <Heading level='h3'>{t('study_courses_program_title')}</Heading>
          </Flex>

          <div className={cx('nextCourseContainer')}>
            <div className={cx('nextCourseCol', 'left')}>
              <Flex direction='row' gap={'small'} alignItems='start'>
                <div className={cx('nowrap')}>
                  <Text variant='contentImportant'>{t('study_courses_program_next_lesson_title')}</Text>
                </div>

                <div className={cx('courseText')}>
                  <Text>{nextCourse?.name}</Text>
                </div>
              </Flex>

              <Flex direction='row' margin='small 0 0 0'>
                <img
                  src={icons.commonIconCalendarDay}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                  className={cx('calendarIcon')}
                />

                <Flex direction='row' gap={'small'}>
                  <Text variant='contentImportant'>{t('study_courses_program_date_title')}</Text>
                  <Text variant='content'>{formattedDate}</Text>
                </Flex>
              </Flex>
            </div>

            <div className={cx('nextCourseCol', 'right')}>
              <img
                src={nextCourse?.teacher?.avatarUrl}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('avatar')}
              />

              <Flex direction='row' margin='small 0 0 0'>
                <img
                  src={icons.commonIconTeacher}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                  className={cx('userIcon')}
                />

                <Flex direction='row' gap={'small'}>
                  <Text variant='contentImportant'>{t('study_courses_program_teacher_name')}</Text>
                  <Text variant='content'>{nextCourse?.teacher?.name}</Text>
                </Flex>
              </Flex>
            </div>
          </div>
        </div>

        <div className={cx('bottomContainer')}>
          <Flex
            display='flex'
            direction='row'
            alignItems='center'
            justifyItems='center'
            width={'100%'}
            margin='0 0 mediumSmall 0'
          >
            <Heading level='h4'>{t('study_courses_program_lesson_coming')}</Heading>
          </Flex>

          <div className={cx('tableCourseContainer')}>
            {courses?.length > 0 ? (
              <CourseTable
                courses={courses}
                onClick={(module: IModule) => handleClickCourseItem(module.courseId, module.id)}
              />
            ) : (
              <div className={cx('centerContainer')}>
                <Flex justifyItems='center' alignItems='center' margin='small 0 0 0'>
                  <Text variant='contentSmall'>{t('course_detail_no_lesson_available', { ns: 'common/home' })}</Text>
                </Flex>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };
  //#endregion Handle Function

  return <div className={cx('container')}>{renderContent()}</div>;
};

export default Course;
